import { EChartService } from './echart.service';
import { StatsCharacterMapping } from '@/models/statistics/stats-character-mapping';
import { Big5 } from '@/models/big5/big5';
import { Big5Score } from '@/models/big5/big5-score';
import { NumberUtils } from '@/utils/number.utils';
import { StatisticsUtils } from '@/utils/statistics.utils';
import { AvgAndSdData, AvgAndSds, AvgAndSd } from '@/models/statistics/stats-boxplot-subtable-data';
import i18n from '@/i18n';

interface ScoreWithUser {
  name: string;
  score: number;
}

export class CharacterBoxplotEchartService extends EChartService {
  self: any;
  val: number;
  big5xLabels: any[];
  subTableColmunsArray: any[];
  subTableDataArray: any[];
  subTableAvgAndSd: AvgAndSdData;

  constructor() {
    super();
    this.series = [];
    this.big5xLabels = [];
    this.subTableColmunsArray = [];
    this.subTableDataArray = [];
    this.subTableAvgAndSd = {};
  }

  get subTableColmuns(): any[] {
    return this.subTableColmunsArray;
  }

  get subTableData(): any[] {
    return this.subTableDataArray;
  }

  createSubTableColumns(nextNumber: number) {
    let cols: any[] = [];

    if (nextNumber > 1) {
      for (let i = 0; i < this.big5xLabels.length; i++) {
        for (let j = 1; j <= nextNumber; j++) {
          const prefix = `${this.serialNumberPrefix}-${j}`;
          cols.push({
            title: this.big5xLabels[i].Title + `(${this.serialNumberPrefix}-${j})`,
            key: prefix + '_' + i,
            width: 160,
            align: 'center',
          });
        }
      }
    } else {
      const prefix = `${this.serialNumberPrefix}-${nextNumber}`;
      cols = this.big5xLabels.map((d: Big5, i: number) => {
        return {
          title: d.Title,
          key: prefix + '_' + i,
          width: 160,
          align: 'center',
        };
      });
    }
    this.subTableColmunsArray = [
      {
        title: '-',
        key: 'name',
        width: 160,
        align: 'center',
      },
    ].concat(cols);
  }

  createSubTableData(data: any, nextNumber: number) {
    // {namae: "最大値", }    最大値  1列目
    // {namae: "75%", }       75%     2列目
    // {namae: "50%", }       50%     3列目
    // {namae: "25%", }       25%     4列目
    // {namae: "最小値", }    最小値  5列目
    // {namae: "効果量", }    効果量  6列目
    if (!this.subTableDataArray.length) {
      this.subTableDataArray = [
        { name: i18n.t('VALUE_MAX').toString() },
        { name: '75%' },
        { name: i18n.t('MEDIAN').toString() },
        { name: '25%' },
        { name: i18n.t('VALUE_MIN').toString() },
        { name: i18n.t('EXAMINEE_COUNT').toString() },
        { name: i18n.t('AVERAGE').toString() },
        { name: i18n.t('STANDARD_DEVIATION').toString() },
        { name: i18n.t('EFFECT_SIZE').toString() },
      ];
    }
    const prefix = `${this.serialNumberPrefix}-${nextNumber}`;
    data.boxData.forEach((box: any, i: number) => {
      if (box.length) {
        const key = prefix + '_' + i;
        const big5 = this.big5xLabels[i];
        const nowSN = `${this.serialNumberPrefix}-${nextNumber}`;
        const nowAvgAndSd = this.subTableAvgAndSd[nowSN][big5.Big5ID];
        this.subTableDataArray[0][key] = box[4];
        this.subTableDataArray[1][key] = box[3];
        this.subTableDataArray[2][key] = box[2];
        this.subTableDataArray[3][key] = box[1];
        this.subTableDataArray[4][key] = box[0];
        this.subTableDataArray[5][key] = nowAvgAndSd.n;
        this.subTableDataArray[6][key] = this.percent(nowAvgAndSd.avg);
        this.subTableDataArray[7][key] = this.percent(nowAvgAndSd.sd);
        // 系列追加が2個め以降であれば、ひとつ前のデータと比較し効果量を算出する
        if (nextNumber > 1) {
          // ひとつ前のデータが undefined であれば算出しない
          const prevSN = `${this.serialNumberPrefix}-${nextNumber - 1}`;
          const prevAvgAndSd = this.subTableAvgAndSd[prevSN][big5.Big5ID];
          if (prevAvgAndSd) {
            const d = NumberUtils.floor(StatisticsUtils.cohen(prevAvgAndSd, nowAvgAndSd), 100);
            this.subTableDataArray[8][key] = d + StatisticsUtils.effectGuide(d);
          } else {
            this.subTableDataArray[8][key] = '';
          }
        } else {
          this.subTableDataArray[8][key] = '';
        }
      }
    });
  }

  get xAxisLabels(): any[] {
    return this.big5xLabels.map((d: Big5) => {
      return {
        value: d.Title,
        textStyle: {
          color: d.color,
          fontSize: 12,
          fontWeight: 'bold',
          borderWidth: 2,
          borderRadius: 1,
        },
      };
    });
  }

  get options() {
    return {
      legend: {
        data: this.labels,
        left: 'center',
      },
      grid: {
        bottom: '25%',
      },
      xAxis: {
        type: 'category',
        data: this.xAxisLabels,
        boundaryGap: true,
        nameGap: 0,
        splitArea: {
          show: true,
        },
        axisLabel: {
          formatter: '{value}',
          rotate: 15,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        min: -100,
        max: 100,
        type: 'value',
        name: i18n.t('PERSONALITY').toString(),
      },
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          show: true,
          height: 20,
          type: 'slider',
          top: '90%',
          xAxisIndex: [0],
          start: 0,
          end: 100,
        },
      ],
      tooltip: {
        formatter: function(params: any) {
          return params.name;
        },
      },
      series: this.series,
    };
  }

  addSeries(chartData: StatsCharacterMapping) {
    const nextNumber: number = this.series.length / 2 + 1;
    const data: any = this.prepareBoxplotData(chartData);
    const serialNumber = `${this.serialNumberPrefix}-${nextNumber}`;
    this.subTableAvgAndSd[serialNumber] = this.prepareBoxplotSubTableData(chartData);
    this.createSubTableColumns(nextNumber);
    this.createSubTableData(data, nextNumber);
    this.series.push({
      name: `${this.serialNumberPrefix}-${nextNumber}`,
      type: 'boxplot',
      data: data.boxData,
      tooltip: {
        formatter: function(param: any) {
          const line1: string = i18n.t('PERSONALITY_NAME').toString() + ` : ${param.name}`;
          const line2: string = i18n.t('VALUE_MAX').toString() + ` : ${param.data[5]}`;
          const line3: string = i18n.t('MEDIAN').toString() + ` : ${param.data[3]}`;
          const line4: string = i18n.t('VALUE_MIN').toString() + ` : ${param.data[1]}`;
          const line5: string = i18n.t('QUARTILE_RANGE').toString() + ` : ${param.data[2]}〜${param.data[4]}`;
          return [line1, line2, line3, line4, line5].join('<br/>');
        },
      },
    });

    this.series.push({
      name: `${this.serialNumberPrefix}-${nextNumber}`,
      type: 'pictorialBar',
      symbolPosition: 'end',
      symbolSize: 6,
      data: data.outliers,
      tooltip: {
        formatter: function(param: any) {
          const line1: string = i18n.t('COMPETENCY_NAME').toString() + ` : ${param.name}`;
          const line2: string = i18n.t('NAME').toString() + ` : ${param.data[2]}`;
          const line3: string = i18n.t('SCORE').toString() + ` : ${param.data[1]}`;
          return [line1, line2, line3].join('<br/>');
        },
      },
      barGap: '30%',
    });
  }

  prepareBoxplotData(data: StatsCharacterMapping) {
    const self: any = this;
    this.big5xLabels = data.big5labels;

    // 各生徒が保持している BIG5 のスコアを分別する
    const scoresList: any = data.data.reduce((result: any, student: any) => {
      student.big5scores.forEach((big5data: Big5Score) => {
        if (!result.hasOwnProperty(big5data.big5ID)) {
          result[big5data.big5ID] = [];
        }
        result[big5data.big5ID].push({ name: student.student.fullName, score: big5data.score, studentID: student.student.studentID } as ScoreWithUser);
      });
      return result;
    }, {});

    // BIG5のスコアを昇順にソート
    for (const big5id in scoresList) {
      scoresList[big5id].sort((a: ScoreWithUser, b: ScoreWithUser) => {
        return a.score - b.score;
      });
    }

    const result: any = { boxData: [], axisData: [], outliers: [] };
    for (const big5Id in scoresList) {
      result.axisData.push(String(big5Id));

      const big5idNumber = Number(big5Id) - 1;

      // データが空なら空配列を入れて終了
      if (scoresList[big5Id].length === 0) {
        result.boxData.push([]);
        return result;
      }

      let measurementLowScore: number;
      let measurementHighScore: number;
      // 第1四分位数
      const q1 = self.quantile(scoresList[big5Id], 0.25);
      // 第2四分位数
      const q2 = self.quantile(scoresList[big5Id], 0.5);
      // 第3四分位数
      const q3 = self.quantile(scoresList[big5Id], 0.75);
      // 最低値
      const min = scoresList[big5Id][0].score;
      // 最高値
      const max = scoresList[big5Id][scoresList[big5Id].length - 1].score;
      const bound = 1.5 * (q3 - q1);
      const virtualLow = Math.max(min, q1 - bound);
      const virtualHigh = Math.min(max, q3 + bound);

      if (virtualLow === min) {
        measurementLowScore = min;
      } else {
        const nextMeasurementLowScoreIndex: number = scoresList[big5Id].findIndex((student: any, index: number) => {
          // 小→大に検索
          if (student.score <= virtualLow) {
            result.outliers.push([big5idNumber, self.percent(student.score), student.name, student.studentID]);
            return false;
          }
          // 最小値を上回ったら終了
          return true;
        });
        result.outliers.pop();
        measurementLowScore = scoresList[big5Id][nextMeasurementLowScoreIndex - 1].score;
      }

      if (virtualHigh === max) {
        measurementHighScore = max;
      } else {
        let i: number = scoresList[big5Id].length - 1;
        for (; i > -1; i--) {
          // 大→小に検索
          if (scoresList[big5Id][i].score >= virtualHigh) {
            result.outliers.push([big5idNumber, self.percent(scoresList[big5Id][i].score), scoresList[big5Id][i].name, scoresList[big5Id][i].studentID]);
            continue;
          }
          // 最大値を下回ったら終了
          break;
        }
        result.outliers.pop();
        measurementHighScore = scoresList[big5Id][i + 1].score;
      }
      result.boxData.push(
        [measurementLowScore, q1, q2, q3, measurementHighScore].map((v: number) => {
          return Math.floor(v * 50);
        })
      );
    }
    return result;
  }

  prepareBoxplotSubTableData(data: StatsCharacterMapping) {
    // 各生徒が保持している BIG5 のスコアを分別する
    const scoresList: any = data.data.reduce((result: any, student: any) => {
      student.big5scores.forEach((big5data: Big5Score) => {
        if (!result.hasOwnProperty(big5data.big5ID)) {
          result[big5data.big5ID] = [];
        }
        result[big5data.big5ID].push({ name: student.student.fullName, score: big5data.score } as ScoreWithUser);
      });
      return result;
    }, {});

    let avgAndSds: AvgAndSds = {};
    for (const big5ID in scoresList) {
      const scoresAarray: number[] = scoresList[big5ID].map((d: ScoreWithUser) => d.score);
      const avg: number = NumberUtils.averageFromArray(scoresAarray);
      const avgAndSd: AvgAndSd = {
        avg: avg,
        sd: NumberUtils.standardDeviation(scoresAarray, avg),
        n: scoresAarray.length,
      };
      avgAndSds[big5ID] = avgAndSd;
    }

    return avgAndSds;
  }

  quantile(ascArr: any[], p: number): number {
    const H = (ascArr.length - 1) * p + 1;
    const h = Math.floor(H);
    const v = +ascArr[h - 1].score;
    const e = H - h;
    return e ? v + e * (ascArr[h].score - v) : v;
  }

  percent(d: number) {
    // 異常値 2より大きい -2より小さい値の場合、最大値を 2 or -2 までとする
    if (d < -2) d = -2;
    if (d > 2) d = 2;
    return Math.floor(d * 50);
  }
}
